import React, { useEffect, useId, useState } from 'react';

export const VERIFIED_SELLER_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];
export const VERIFIED_SELLER_MAX_FILE_SIZE = 15 * 1024 * 1024;

export const VERIFIED_SELLER_FIELDS = [
  {
    name: 'legalName',
    type: 'text',
    label: 'Legal name',
    required: true,
    placeholder: 'Your full legal name',
  },
  {
    name: 'dba',
    type: 'text',
    label: 'Doing business as (optional)',
    placeholder: 'Your business or storefront name',
  },
  {
    name: 'govId',
    type: 'file',
    label: 'Government ID',
    required: true,
    help: 'Upload the front of a government-issued photo ID.',
  },
  {
    name: 'selfie',
    type: 'file',
    label: 'Selfie',
    required: true,
    help: 'Upload a clear selfie so the reviewer can compare it with your ID.',
  },
];

const DEFAULT_COPY = {
  heading: 'Submit identity verification',
  introduction: 'Your encrypted documents are reviewed by Mayhem administrators.',
  retention: 'Submitted identity images are retained securely for trust and safety purposes.',
  submitLabel: 'Submit for review',
};

function createPreview(file) {
  if (!file || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null;
  }
  return URL.createObjectURL(file);
}

export default function VerifiedSellerForm({
  fields = VERIFIED_SELLER_FIELDS,
  copy = DEFAULT_COPY,
  initialValues = {},
  disabled = false,
  submitting = false,
  onSubmit,
}) {
  const formId = useId();
  const [values, setValues] = useState(initialValues);
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});
  const [error, setError] = useState(null);
  const resolvedCopy = { ...DEFAULT_COPY, ...copy };

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues.legalName, initialValues.dba]);

  useEffect(() => () => {
    Object.values(previews).forEach(preview => {
      if (preview && typeof URL !== 'undefined'
        && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(preview);
      }
    });
  }, [previews]);

  const handleFileChange = (field, file) => {
    const oldPreview = previews[field.name];
    if (oldPreview && typeof URL !== 'undefined'
      && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(oldPreview);
    }
    setFiles(current => ({ ...current, [field.name]: file || null }));
    setPreviews(current => ({ ...current, [field.name]: createPreview(file) }));
    setError(null);
  };

  const validate = () => {
    for (const field of fields) {
      if (field.type === 'file') {
        const file = files[field.name];
        if (field.required && !file) return `${field.label} is required.`;
        if (!file) continue;
        if (!VERIFIED_SELLER_MIME_TYPES.includes(file.type)) {
          return `${field.label} must be a JPEG, PNG, WebP, HEIC, or HEIF image.`;
        }
        if (file.size > VERIFIED_SELLER_MAX_FILE_SIZE) {
          return `${field.label} must be 15 MB or smaller.`;
        }
      } else if (field.required && !String(values[field.name] || '').trim()) {
        return `${field.label} is required.`;
      }
    }

    const legalName = String(values.legalName || '').trim();
    if (fields.some(field => field.name === 'legalName')
      && (legalName.length < 2 || legalName.length > 120)) {
      return 'Legal name must be between 2 and 120 characters.';
    }
    return null;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const formData = new FormData();
    fields.forEach(field => {
      if (field.type === 'file') {
        if (files[field.name]) formData.append(field.name, files[field.name]);
      } else {
        formData.append(field.name, String(values[field.name] || '').trim());
      }
    });

    setError(null);
    try {
      await onSubmit?.(formData);
    } catch (submitError) {
      setError(submitError.message || 'Verification could not be submitted.');
    }
  };

  return (
    <form className="verified-seller-form" onSubmit={handleSubmit} noValidate>
      <div className="verified-seller-form__heading">
        <h3>{resolvedCopy.heading}</h3>
        <p>{resolvedCopy.introduction}</p>
      </div>

      {error && <div className="verification-inline-error" role="alert">{error}</div>}

      {fields.map(field => {
        const id = `${formId}-${field.name}`;
        return (
          <div className="verification-field" key={field.name}>
            <label htmlFor={id}>{field.label}</label>
            {field.type === 'file' ? (
              <>
                <input
                  id={id}
                  name={field.name}
                  type="file"
                  accept={VERIFIED_SELLER_MIME_TYPES.join(',')}
                  required={field.required}
                  disabled={disabled || submitting}
                  onChange={event => handleFileChange(field, event.target.files?.[0])}
                />
                {previews[field.name] && (
                  <img
                    className="verification-image-preview"
                    src={previews[field.name]}
                    alt={`${field.label} preview`}
                  />
                )}
              </>
            ) : (
              <input
                id={id}
                name={field.name}
                type={field.type}
                value={values[field.name] || ''}
                placeholder={field.placeholder}
                required={field.required}
                disabled={disabled || submitting}
                onChange={event => setValues(current => ({
                  ...current,
                  [field.name]: event.target.value,
                }))}
              />
            )}
            {field.help && <p className="help">{field.help}</p>}
          </div>
        );
      })}

      <p className="verification-retention-notice">
        <i className="fa-solid fa-lock" aria-hidden="true"></i>
        {resolvedCopy.retention}
      </p>
      <button type="submit" disabled={disabled || submitting}>
        {submitting ? 'Submitting…' : resolvedCopy.submitLabel}
      </button>
    </form>
  );
}
